import type { components } from "@/api-schema";
import { cn } from "@/common/helpers/utils";
import { MapThemePalette, rgbaCss } from "@/components/deck-map/_helpers/map-theme";
import { RELOCATION_ORDER_STATUS_TRANSLATIONS } from "@/components/relocation-order/relocation-order-status-translations";
import { Badge, BadgeColorVariant, BadgeVariant } from "@/components/ui/badge";
import { useLayoutEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import { ARC_COLORS, StatusCategory } from "../map-statuses";

export enum TooltipType {
  Branch = "branch",
  Arc = "arc",
}

export type TooltipInfo =
  | {
      x: number;
      y: number;
      type: TooltipType.Branch;
      name: string;
      incoming: number;
      outgoing: number;
    }
  | {
      x: number;
      y: number;
      type: TooltipType.Arc;
      from: string;
      current: string | null;
      to: string;
      vin: string;
      vehicle: string;
      status: components["schemas"]["RelocationOrder"]["status"];
      statusCategory: StatusCategory;
    };

interface MapTooltipProps {
  info: TooltipInfo | null;
  palette: MapThemePalette;
}

export function MapTooltip({ info, palette }: MapTooltipProps) {
  const { t } = useTranslation(["fit", "common"]);
  const tooltipRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const tooltip = tooltipRef.current;
    const container = tooltip?.parentElement;

    if (!tooltip || !container || !info) return;

    const margin = 12;
    const maxLeft = Math.max(margin, container.clientWidth - tooltip.offsetWidth - margin);
    const maxTop = Math.max(margin, container.clientHeight - tooltip.offsetHeight - margin);

    tooltip.style.left = `${Math.max(margin, Math.min(info.x + margin, maxLeft))}px`;
    tooltip.style.top = `${Math.max(margin, Math.min(info.y - margin, maxTop))}px`;
  }, [info, t]);

  if (!info) return null;

  return (
    <div
      ref={tooltipRef}
      className={cn(
        "pointer-events-none absolute z-50 border text-sm break-words backdrop-blur-md",
        info.type === TooltipType.Arc
          ? "w-80 max-w-[calc(100%-1.5rem)] rounded-xl p-4"
          : "max-w-72 rounded-lg px-3 py-2.5",
      )}
      style={{
        left: info.x + 12,
        top: info.y - 12,
        backgroundColor: palette.surface.tooltipBackground,
        borderColor: palette.surface.border,
        color: palette.text.primary,
        boxShadow: palette.surface.shadow,
      }}
    >
      {info.type === TooltipType.Branch ? (
        <div className="flex flex-col gap-1.5">
          <span className="font-semibold">{info.name}</span>
          <div className="flex gap-3 text-xs" style={{ color: palette.text.secondary }}>
            <span>
              ↗ {t("fit:Outgoing")}: {info.outgoing}
            </span>
            <span>
              ↙ {t("fit:Incoming")}: {info.incoming}
            </span>
          </div>
        </div>
      ) : (
        <div className="flex min-w-0 flex-col gap-3">
          <div className="flex min-w-0 flex-col gap-1">
            {info.vehicle && <span className="leading-snug font-semibold">{info.vehicle}</span>}
            <span className="font-mono text-xs" style={{ color: palette.text.muted }}>
              {info.vin}
            </span>
          </div>
          <Badge
            variant={BadgeVariant.Outlined}
            colorVariant={BadgeColorVariant.Secondary}
            className="max-w-full self-start px-2 py-1 text-xs leading-snug whitespace-normal"
            style={getStatusBadgeStyle(info.statusCategory, palette.mode)}
          >
            {t(RELOCATION_ORDER_STATUS_TRANSLATIONS[info.status])}
          </Badge>
          <dl
            className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-2.5 border-t pt-3 text-xs"
            style={{ borderColor: palette.surface.separator }}
          >
            <dt style={{ color: palette.text.muted }}>{t("fit:From")}</dt>
            <dd className="min-w-0 font-medium">{info.from}</dd>
            {info.current && (
              <>
                <dt style={{ color: palette.text.muted }}>{t("fit:CurrentBranch")}</dt>
                <dd className="min-w-0 font-medium" style={{ color: palette.accent.primary }}>
                  {info.current}
                </dd>
              </>
            )}
            <dt style={{ color: palette.text.muted }}>{t("fit:To")}</dt>
            <dd className="min-w-0 font-medium">{info.to}</dd>
          </dl>
        </div>
      )}
    </div>
  );
}

function getStatusBadgeStyle(statusCategory: StatusCategory, mode: MapThemePalette["mode"]) {
  const statusColor = ARC_COLORS[statusCategory];

  return {
    backgroundColor: rgbaCss(statusColor, mode === "dark" ? 0.18 : 0.12),
    borderColor: rgbaCss(statusColor, mode === "dark" ? 0.42 : 0.3),
    color: mode === "dark" ? rgbaCss(statusColor, 1) : `color-mix(in srgb, ${rgbaCss(statusColor, 1)} 50%, black)`,
  };
}
